import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, HardHat, ShieldCheck, Lightning, UsersThree } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { syncBuyerProfileToDraft } from "@/lib/buyerProfile";

function buyerReturnPath(search) {
  const params = new URLSearchParams(search || "");
  const next = params.get("next");
  if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  return "/account";
}

const FEATURES = [
  { icon: ShieldCheck, text: "Verified supplier network across India" },
  { icon: Lightning, text: "Get quotes within 24 hours" },
  { icon: UsersThree, text: "Free for buyers, always" },
];

function UnderlineInput({ label, id, type = "text", placeholder, value, onChange, onKeyDown, testId, autoComplete }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[10px] font-bold tracking-[0.15em] uppercase mb-2 transition-colors duration-150"
        style={{ color: focused ? "#FF7A00" : "#aaa" }}
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          data-testid={testId}
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          autoComplete={autoComplete}
          className="w-full bg-transparent text-neutral-900 text-sm py-2.5 outline-none placeholder-neutral-400"
          style={{ borderBottom: `2px solid ${focused ? "#FF7A00" : "#D8D2C8"}`, transition: "border-color 0.2s" }}
        />
      </div>
    </div>
  );
}

export default function BuyerLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, registerBuyer, logout, user } = useAuth();
  const [tab, setTab] = useState("login");
  const [loading, setLoading] = useState(false);

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regCompany, setRegCompany] = useState("");

  useEffect(() => {
    if (user?.role === "buyer") {
      navigate(buyerReturnPath(location.search), { replace: true });
    }
  }, [user, location.search, navigate]);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) { toast.error("Fill all fields"); return; }
    setLoading(true);
    try {
      const u = await login(loginEmail, loginPassword);
      if (u.role !== "buyer") {
        logout();
        if (u.role === "supplier") toast.error("Supplier accounts use the supplier portal.");
        else if (u.role === "admin") toast.error("Admin accounts use the admin sign-in page.");
        else toast.error("Use the staff sign-in page for staff accounts.");
        setLoading(false);
        return;
      }
      syncBuyerProfileToDraft(u);
      toast.success("Welcome back!");
      navigate(buyerReturnPath(location.search));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const handleRegister = async () => {
    if (!regName || !regEmail || !regPassword || !regPhone) { toast.error("Fill all required fields"); return; }
    if (regPhone.trim().length < 10) { toast.error("Enter a valid 10-digit phone number"); return; }
    setLoading(true);
    try {
      const u = await registerBuyer({
        name: regName.trim(),
        email: regEmail.trim(),
        password: regPassword,
        phone: regPhone.trim(),
        company_name: regCompany.trim(),
      });
      syncBuyerProfileToDraft(u);
      toast.success("Account created");
      navigate(buyerReturnPath(location.search));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#F7F4EF" }}>

      {/* ── LEFT BRANDING PANEL ──────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[45%] shrink-0 relative overflow-hidden p-12 xl:p-16"
        style={{ background: "linear-gradient(145deg, #1a1a1a 0%, #2d1a08 60%, #1a1a1a 100%)" }}
      >
        {/* Grid texture */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(#FF7A00 1px, transparent 1px), linear-gradient(90deg, #FF7A00 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="absolute top-0 left-0 bottom-0 w-1 bg-[#FF7A00]" />

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <HardHat size={28} weight="bold" style={{ color: "#FF7A00" }} />
          <span className="text-white font-black tracking-tight text-lg">SCAFFOINDIA</span>
        </div>

        {/* Main copy */}
        <div className="relative">
          <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-5" style={{ color: "#FF7A00" }}>
            Buyer portal
          </p>
          <h2
            className="font-black text-white leading-none tracking-tighter mb-6"
            style={{ fontSize: "clamp(2.2rem, 3.5vw, 3.4rem)" }}
          >
            Source smarter.<br />
            <span style={{ color: "#FF7A00" }}>Build faster.</span>
          </h2>
          <p className="text-sm leading-relaxed mb-10" style={{ color: "#888", maxWidth: "340px" }}>
            Sign in to save your details, track requirements, and auto-fill your next enquiry in seconds.
          </p>

          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: "rgba(255,122,0,0.15)" }}
                >
                  <Icon size={16} weight="bold" style={{ color: "#FF7A00" }} />
                </div>
                <p className="text-sm font-medium" style={{ color: "#bbb" }}>{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom note */}
        <div className="relative">
          <p className="text-xs" style={{ color: "#555" }}>
            Supplier?{" "}
            <Link to="/supplier/login" className="font-bold underline-offset-2 underline" style={{ color: "#FF7A00" }}>
              Use the supplier portal
            </Link>
          </p>
        </div>
      </div>

      {/* ── RIGHT FORM PANEL ─────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-h-screen overflow-y-auto" style={{ background: "#F7F4EF" }}>

        {/* Mobile top bar */}
        <div className="flex items-center justify-between px-6 py-5 lg:hidden border-b" style={{ borderColor: "#E8E3D9" }}>
          <div className="flex items-center gap-2">
            <HardHat size={22} weight="bold" style={{ color: "#FF7A00" }} />
            <span className="text-neutral-900 font-black tracking-tight">SCAFFOINDIA</span>
          </div>
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="buyer-login-back-home"
            className="flex items-center gap-1.5 text-xs font-semibold transition-colors hover:opacity-80"
            style={{ color: "#999" }}
          >
            <ArrowLeft size={14} weight="bold" /> Home
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-12 sm:px-10 lg:px-16 xl:px-20">
          <div className="w-full max-w-md">

            {/* Desktop back link */}
            <button
              type="button"
              onClick={() => navigate("/")}
              data-testid="buyer-login-back-home"
              className="hidden lg:flex items-center gap-1.5 text-xs font-semibold mb-10 transition-colors hover:opacity-80"
              style={{ color: "#aaa" }}
            >
              <ArrowLeft size={13} weight="bold" /> Back to home
            </button>

            {/* Tab switcher */}
            <div className="flex items-center gap-1 mb-10 p-1 rounded-full" style={{ background: "#EDE8E0", border: "1px solid #DDD8CE" }}>
              {["login", "register"].map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={t === "login" ? "buyer-login-tab" : "buyer-register-tab"}
                  onClick={() => setTab(t)}
                  className="flex-1 py-2.5 rounded-full text-sm font-bold transition-all duration-200"
                  style={{
                    background: tab === t ? "#FF7A00" : "transparent",
                    color: tab === t ? "#fff" : "#999",
                  }}
                >
                  {t === "login" ? "Sign in" : "Register"}
                </button>
              ))}
            </div>

            {/* Heading */}
            <div className="mb-8">
              <h1
                className="font-black text-neutral-900 leading-tight tracking-tighter"
                style={{ fontSize: "clamp(1.6rem, 3vw, 2.2rem)" }}
              >
                {tab === "login" ? "Welcome back." : "Create your account."}
              </h1>
              <p className="mt-2 text-sm" style={{ color: "#999" }}>
                {tab === "login"
                  ? "Sign in to your buyer account"
                  : "Free for buyers — get started in seconds"}
              </p>
            </div>

            {/* LOGIN FORM */}
            {tab === "login" && (
              <div className="space-y-6">
                <UnderlineInput
                  id="login-email"
                  label="Email"
                  type="email"
                  placeholder="your@email.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  testId="buyer-login-email"
                  autoComplete="email"
                />
                <UnderlineInput
                  id="login-password"
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  testId="buyer-login-password"
                  autoComplete="current-password"
                />
                <button
                  data-testid="buyer-login-submit"
                  type="button"
                  onClick={handleLogin}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-black transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  style={{ background: "#FF7A00", color: "#fff" }}
                >
                  {loading ? "Signing in…" : <>Sign in <ArrowRight size={15} weight="bold" /></>}
                </button>
              </div>
            )}

            {/* REGISTER FORM */}
            {tab === "register" && (
              <div className="space-y-5">
                <UnderlineInput
                  id="reg-name"
                  label="Full name *"
                  placeholder="Your name"
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  testId="buyer-reg-name"
                  autoComplete="name"
                />
                <div className="grid grid-cols-2 gap-5">
                  <UnderlineInput
                    id="reg-email"
                    label="Email *"
                    type="email"
                    placeholder="your@email.com"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    testId="buyer-reg-email"
                    autoComplete="email"
                  />
                  <UnderlineInput
                    id="reg-phone"
                    label="Phone *"
                    placeholder="10-digit number"
                    value={regPhone}
                    onChange={(e) => setRegPhone(e.target.value)}
                    testId="buyer-reg-phone"
                    autoComplete="tel"
                  />
                </div>
                <UnderlineInput
                  id="reg-password"
                  label="Password *"
                  type="password"
                  placeholder="Create a password"
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  testId="buyer-reg-password"
                  autoComplete="new-password"
                />
                <UnderlineInput
                  id="reg-company"
                  label="Company (optional)"
                  placeholder="Your company name"
                  value={regCompany}
                  onChange={(e) => setRegCompany(e.target.value)}
                  testId="buyer-reg-company"
                  autoComplete="organization"
                />
                <button
                  data-testid="buyer-register-submit"
                  type="button"
                  onClick={handleRegister}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full text-sm font-black transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                  style={{ background: "#FF7A00", color: "#fff" }}
                >
                  {loading ? "Creating account…" : <>Create account <ArrowRight size={15} weight="bold" /></>}
                </button>
              </div>
            )}

            {/* Mobile supplier link */}
            <p className="mt-8 text-sm text-center lg:hidden" style={{ color: "#aaa" }}>
              Supplier?{" "}
              <Link to="/supplier/login" className="font-bold" style={{ color: "#FF7A00" }}>
                Supplier portal
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
